import { useCallback } from "react";
import { View } from "@src/components";
import { Button } from "@src/components/Button";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { showInfoToast, showErrorToast } from "@src/utils/toast";
import {
  useLatestRoadSenseReportForSite,
  useRegenerateRoadSenseLatest,
} from "@src/features/roadsense/hooks/useRegenerateRoadSenseLatest";

interface TrafficFlowReportActionsProps {
  readonly tenantId: string;
  readonly siteId: string;
}

/**
 * Web-only RoadSense report actions for the Crossline Mapping config page:
 *
 * - **Regenerate Report** — fire-and-forget re-enumerate + regenerate of the
 *   tenant's most-recent report from live scenario/subscription data (so an
 *   operator's exclusion/geolocation edit lands immediately rather than at the
 *   next daily tick). We only toast "being regenerated"; no polling.
 * - **View Report** — opens the most-recent report's per-site detail page in a
 *   new tab. Hidden until a ready report resolves that actually contains this
 *   site (otherwise the deep link would 404/land on an empty site).
 *
 * Both are gated behind `REPORTS_ROADSENSE` and are web-only (this file has the
 * `.web.tsx` suffix, matching the reports screens' web-only rendering).
 */
export function TrafficFlowReportActions({ tenantId, siteId }: TrafficFlowReportActionsProps) {
  return (
    <FeatureGate flag={FEATURE_FLAGS.REPORTS_ROADSENSE}>
      <TrafficFlowReportActionsInner tenantId={tenantId} siteId={siteId} />
    </FeatureGate>
  );
}

// Split out so the data-fetching hooks only run when the flag is enabled
// (hooks can't be called conditionally, so the gate must wrap a child).
function TrafficFlowReportActionsInner({ tenantId, siteId }: TrafficFlowReportActionsProps) {
  const { reportId, siteInReport } = useLatestRoadSenseReportForSite(siteId);
  const { regenerate, isPending } = useRegenerateRoadSenseLatest();

  const onRegenerate = useCallback(() => {
    // Fire-and-forget: kick off the rebuild and immediately reassure the
    // operator. We deliberately don't await/poll — the generator upserts the
    // same report row, so the existing report stays viewable meanwhile.
    void regenerate()
      .then(() => {
        showInfoToast({
          message: "Regenerating report",
          description: "Your report is being regenerated and will be available shortly.",
        });
      })
      .catch((error: unknown) => {
        showErrorToast({ error });
      });
  }, [regenerate]);

  const onView = useCallback(() => {
    if (!reportId) return;
    // `window.open` is a raw browser navigation that bypasses Expo Router, so —
    // unlike `<Link>`/`href` — it does NOT get the exported web `baseUrl`
    // ("/app") prepended for us. Production web is served under /app (see
    // `experiments.baseUrl` in app.config.ts), so omitting it lands on an
    // unmatched route and the SPA falls back to the tenant overview. Detect and
    // preserve the base path exactly as TenantProvider does for its full-page
    // tenant-switch navigation.
    const basePathPrefix =
      (
        typeof window !== "undefined" &&
        (window.location.pathname === "/app" || window.location.pathname.startsWith("/app/"))
      ) ?
        "/app"
      : "";
    const url = `${basePathPrefix}/tenants/${tenantId}/reports/roadsense/report/${encodeURIComponent(reportId)}/site/${encodeURIComponent(siteId)}`;
    window.open(url, "_blank", "noopener");
  }, [tenantId, siteId, reportId]);

  const canView = !!reportId && siteInReport;

  return (
    <View style={{ flexDirection: "row", gap: 12, flexWrap: "wrap" }}>
      <Button
        label="Regenerate Report"
        variant="secondary"
        variantColor="blue"
        onPress={onRegenerate}
        loading={isPending}
        disabled={isPending}
        accessibilityLabel="Regenerate RoadSense report"
      />
      {canView ?
        <Button
          label="View Report"
          variant="secondary"
          variantColor="grey"
          onPress={onView}
          accessibilityLabel="View RoadSense report in a new tab"
        />
      : null}
    </View>
  );
}
